import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger, Field } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { IFileDto } from '../../../../config/fileConfig'
import { MainDto } from '../../../../config/filePartyConfig'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { DialogBodyProps } from '@fluentui/react-dialog'
import FormContainer from '../../../../components/Form/FormContainer'
import { HammockDialogProps } from '../HammockDialog'
import FilePartyCombobox, { FilePartyType } from '../../Combobox/FilePartyCombobox'
import { PartyRecipientDto } from '../../../../config/partyConfig'

export type ComposeOutlookItemDialogBodyProps = DialogBodyProps & {
    titleKey: string
    descriptionKey: string
    submitButtonTextKey: string

    file: IFileDto
    parties: MainDto[]
    onCompose: (recipients: Office.EmailAddressDetails[], subject: string) => Promise<void>
    callback?: () => void
}

export const formatRecipients = (recipients: PartyRecipientDto[]): Office.EmailAddressDetails[] =>
    recipients.flatMap(
        party =>
            (party.email || '').split(/[,;]/).map(email => ({
                displayName: party.displayName,
                emailAddress: email?.trim(),
            })) as Office.EmailAddressDetails[],
    )

export const formatSubject = (file: IFileDto, recipients: FilePartyType[]): string => {
    const references: string[] = [file.reference, ...recipients.filter(party => !!party['fileReference']).map(party => party['fileReference'])]
    return `${file.name} ${[...new Set(references)].map(reference => `[${reference}]`).join(' - ')}`
}

export default function ComposeOutlookItemDialogBody<BodyPropsT extends ComposeOutlookItemDialogBodyProps>(
    props: HammockDialogProps<BodyPropsT>,
): ReactElement {
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as BodyPropsT), [props])

    const [filePartyIds, setFilePartyIds] = useState<string[]>([])
    const [selectedFileParties, setFileParties] = useState<FilePartyType[]>([])

    return (
        <DialogBody>
            <DialogTitle>{translate(dialogBodyProps?.titleKey)}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    <div>{dialogBodyProps?.descriptionKey && translate(dialogBodyProps?.descriptionKey)}</div>

                    <Field
                        label={translate('email.compose.recipient', { count: filePartyIds.length })}
                        orientation={viewportSmall ? 'vertical' : 'horizontal'}
                    >
                        <FilePartyCombobox
                            multiselect
                            entities={dialogBodyProps?.parties || []}
                            fileId={dialogBodyProps.file.id}
                            isEntityDisabled={entity => (entity.email || '').length === 0}
                            selectedOptions={filePartyIds}
                            onOptionSelect={(_event, data, selectedEntities) => {
                                setFilePartyIds(data.selectedOptions)
                                setFileParties(selectedEntities)
                            }}
                        />
                    </Field>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button
                    appearance="primary"
                    onClick={() => {
                        dialogBodyProps
                            ?.onCompose?.(formatRecipients(selectedFileParties), formatSubject(dialogBodyProps?.file, selectedFileParties))
                            ?.then(() => {
                                setFilePartyIds([])
                                setFileParties([])
                            })
                    }}
                >
                    {translate(dialogBodyProps?.submitButtonTextKey || 'action.save')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
